import React from 'react';
import { useAuth } from '../../context/AuthContext';
import AccessDenied from '../../pages/AccessDenied';

interface RoleGuardProps {
    /**
     * User must have at least one of these roles to see the children.
     * Pass multiple roles to allow any of them: roles={['admin', 'editor']}
     */
    roles: string[];
    /** Custom fallback rendered when the user lacks the required role.
     *  Defaults to <AccessDenied />. */
    fallback?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * RoleGuard — conditionally renders children based on the current user's role.
 *
 * Usage (page-level):
 *   <RoleGuard roles={['admin']}>
 *     <AdminPage />
 *   </RoleGuard>
 *
 * Usage (inline, e.g. hide a button for non-admins):
 *   <RoleGuard roles={['admin']} fallback={null}>
 *     <DeleteButton />
 *   </RoleGuard>
 *
 * Roles are always passed as props — nothing is hardcoded in this component.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({
    roles,
    fallback = <AccessDenied />,
    children,
}) => {
    const { hasAnyRole, isAuthenticated } = useAuth();

    // Not logged in — ProtectedRoute handles the redirect, but guard gracefully
    if (!isAuthenticated) return null;

    if (!hasAnyRole(roles)) {
        return <>{fallback}</>;
    }

    return <>{children}</>;
};

export default RoleGuard;
